import { useMemo, useRef, useState } from "react";

import { formatMoney, formatNumber } from "./licensingTheme";
import { useBarsRise } from "./licensingMotion";
import { ChartTooltip, useChartTooltip } from "./ChartTooltip";

const COLORS = { new: "#6fbf9a", renewal: "#227f5d", refund: "#6f42c1" };

/** A round number at or above `value`, so the axis reads $50 / $100 rather than $47.31. */
function niceCeil(value) {
  if (value <= 0) return 1;
  const exponent = 10 ** Math.floor(Math.log10(value));
  const fraction = value / exponent;
  const step = fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 2.5 ? 2.5 : fraction <= 5 ? 5 : 10;
  return step * exponent;
}

/**
 * Money in, by period and by where it came from.
 *
 * New purchases and renewals stack, because together they are what was
 * collected. Refunds hang below the line, because they are money going back
 * out and folding them into the bar would hide them. Practice (MOCK) payments
 * never enter these totals.
 */
export default function RevenueChart({ overview, loading, onDrill }) {
  const scope = useRef(null);
  const { tip, bind, hide } = useChartTooltip();
  const [currency, setCurrency] = useState(null);

  const currencies = Object.keys(overview?.revenueByCurrency ?? {});
  const active = currency || overview?.currency || currencies[0] || "USD";
  const bucket = overview?.range?.bucket ?? "day";
  const revenueKey = JSON.stringify(overview?.revenue ?? []);

  const buckets = useMemo(() => {
    const byBucket = new Map();
    for (const row of overview?.revenue ?? []) {
      if (row.currency && row.currency !== active) continue;
      const entry = byBucket.get(row.bucket) ?? { bucket: row.bucket, newCents: 0, renewalCents: 0, refundCents: 0, testCents: 0 };
      entry.newCents += Number(row.newCents) || 0;
      entry.renewalCents += Number(row.renewalCents) || 0;
      entry.refundCents += Number(row.refundCents) || 0;
      entry.testCents += Number(row.testCents) || 0;
      byBucket.set(row.bucket, entry);
    }
    return Array.from(byBucket.values()).sort((a, b) => a.bucket.localeCompare(b.bucket));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revenueKey, active]);

  useBarsRise(scope, [revenueKey, active], { stagger: 0.012 });

  const top = niceCeil(Math.max(0, ...buckets.map((b) => b.newCents + b.renewalCents)));
  const bottom = Math.max(0, ...buckets.map((b) => Math.abs(b.refundCents)));
  const refundBand = bottom ? 22 : 0; // percent of the plot kept below the line
  const totals = buckets.reduce(
    (acc, b) => ({
      newCents: acc.newCents + b.newCents,
      renewalCents: acc.renewalCents + b.renewalCents,
      refundCents: acc.refundCents + b.refundCents,
      testCents: acc.testCents + b.testCents,
    }),
    { newCents: 0, renewalCents: 0, refundCents: 0, testCents: 0 },
  );
  const collected = totals.newCents + totals.renewalCents + totals.refundCents;
  const status = overview?.paymentStatus ?? {};

  const periodLabel = (key, long = false) => {
    const date = new Date(`${key}T00:00:00Z`);
    if (bucket === "month") return date.toLocaleDateString(undefined, { month: long ? "long" : "short", year: "numeric", timeZone: "UTC" });
    const text = date.toLocaleDateString(undefined, { day: "numeric", month: "short", timeZone: "UTC" });
    return bucket === "week" && long ? `Week of ${text}` : text;
  };
  const labelEvery = Math.max(1, Math.ceil(buckets.length / 8));
  const money = (cents) => formatMoney(cents, active, { compact: true });

  return (
    <section className="mcap-lic-panel" aria-labelledby="mcap-lic-revenue-title" ref={scope}>
      <header className="mcap-lic-panel-head">
        <div>
          <h3 id="mcap-lic-revenue-title" className="mcap-lic-panel-title">
            Revenue
          </h3>
          <p className="mcap-lic-panel-sub">Money in from teachers and schools, by {bucket}.</p>
        </div>
        {currencies.length > 1 && (
          <div className="mcap-lic-currency" role="group" aria-label="Currency">
            {currencies.map((code) => (
              <button key={code} type="button" className={code === active ? "is-active" : ""} onClick={() => setCurrency(code)}>
                {code}
              </button>
            ))}
          </div>
        )}
      </header>

      <div className="mcap-lic-stat-strip">
        <div className="mcap-lic-stat is-money">
          <span className="mcap-lic-stat-value">{loading ? "—" : formatMoney(collected, active)}</span>
          <span className="mcap-lic-stat-label">Total, after refunds</span>
        </div>
        <button type="button" className="mcap-lic-stat" onClick={() => onDrill({ type: "payments", title: "New plans", filters: { kind: "new" } })}>
          <span className="mcap-lic-stat-value">
            <span className="mcap-lic-legend-dot" style={{ background: COLORS.new }} /> {loading ? "—" : formatMoney(totals.newCents, active)}
          </span>
          <span className="mcap-lic-stat-label">New plans</span>
        </button>
        <button type="button" className="mcap-lic-stat" onClick={() => onDrill({ type: "payments", title: "Renewals", filters: { kind: "renewal" } })}>
          <span className="mcap-lic-stat-value">
            <span className="mcap-lic-legend-dot" style={{ background: COLORS.renewal }} /> {loading ? "—" : formatMoney(totals.renewalCents, active)}
          </span>
          <span className="mcap-lic-stat-label">Renewals</span>
        </button>
        <button type="button" className="mcap-lic-stat" onClick={() => onDrill({ type: "payments", title: "Refunds", filters: { kind: "refund" } })}>
          <span className="mcap-lic-stat-value">
            <span className="mcap-lic-legend-dot" style={{ background: COLORS.refund }} /> {loading ? "—" : formatMoney(Math.abs(totals.refundCents), active)}
          </span>
          <span className="mcap-lic-stat-label">Refunded</span>
        </button>
      </div>

      {!loading && buckets.length > 0 && (
        <div className="mcap-lic-plot">
          <div className="mcap-lic-plot-axis" aria-hidden="true">
            {[1, 0.5, 0].map((f) => (
              <span key={f} style={{ bottom: `${refundBand + f * (100 - refundBand)}%` }}>
                {money(top * f)}
              </span>
            ))}
          </div>
          <div className="mcap-lic-plot-area">
            {[1, 0.5].map((f) => (
              <span key={f} className="mcap-lic-plot-grid" style={{ bottom: `${refundBand + f * (100 - refundBand)}%` }} />
            ))}
            <span className="mcap-lic-plot-zero" style={{ bottom: `${refundBand}%` }} />
            <div className="mcap-lic-plot-cols">
              {buckets.map((entry, index) => {
                const up = entry.newCents + entry.renewalCents;
                const upHeight = (up / top) * (100 - refundBand);
                const downHeight = bottom ? (Math.abs(entry.refundCents) / bottom) * refundBand * 0.9 : 0;
                const has = up > 0 || entry.refundCents !== 0;
                return (
                  <button
                    key={entry.bucket}
                    type="button"
                    className={`mcap-lic-plot-col ${has ? "" : "is-empty"}`}
                    {...bind({
                      title: periodLabel(entry.bucket, true),
                      rows: [
                        { label: "New plans", value: formatMoney(entry.newCents, active), color: COLORS.new },
                        { label: "Renewals", value: formatMoney(entry.renewalCents, active), color: COLORS.renewal },
                        ...(entry.refundCents ? [{ label: "Refunded", value: formatMoney(entry.refundCents, active), color: COLORS.refund }] : []),
                        { label: "Net", value: formatMoney(up + entry.refundCents, active) },
                        ...(entry.testCents ? [{ label: "Test payments (not counted)", value: formatMoney(entry.testCents, active), muted: true }] : []),
                      ],
                      footer: has && bucket === "day" ? "Click to see payments" : undefined,
                    })}
                    onClick={() => {
                      if (!has || bucket !== "day") return;
                      hide();
                      onDrill({ type: "payments", title: `Payments on ${periodLabel(entry.bucket)}`, filters: { day: entry.bucket, currency: active } });
                    }}
                    aria-label={`${periodLabel(entry.bucket, true)}: ${formatMoney(up + entry.refundCents, active)} net`}
                  >
                    {up > 0 && (
                      <span className="mcap-lic-plot-up" data-bar style={{ bottom: `${refundBand}%`, height: `${Math.max(1.5, upHeight)}%` }}>
                        {entry.newCents > 0 && <span style={{ flex: entry.newCents, background: COLORS.new }} />}
                        {entry.renewalCents > 0 && <span style={{ flex: entry.renewalCents, background: COLORS.renewal }} />}
                      </span>
                    )}
                    {downHeight > 0 && (
                      <span className="mcap-lic-plot-down" style={{ top: `${100 - refundBand}%`, height: `${downHeight}%`, background: COLORS.refund }} />
                    )}
                    <span className="mcap-lic-plot-label">{index % labelEvery === 0 ? periodLabel(entry.bucket) : ""}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {!loading && collected === 0 && totals.testCents === 0 && (
        <p className="mcap-lic-empty">No money came in during these dates.</p>
      )}

      <p className="mcap-lic-journey-heading mcap-lic-status-heading">Payment results</p>
      <div className="mcap-lic-status-row">
        {[
          { key: "succeeded", label: "Paid", kind: "all", tone: "good", detail: "Payments that went through, including school licences." },
          { key: "failedPeriods", label: "Failed", kind: "failed", tone: "risk", detail: `Teachers whose card was declined (${formatNumber(status.failedAttempts ?? 0)} tries in all).` },
          { key: "pending", label: "Processing", kind: "pending", tone: "", detail: "Waiting for the bank to confirm." },
          { key: "abandoned", label: "Left checkout", kind: "abandoned", tone: "warn", detail: "Started paying but didn't finish." },
          { key: "refunds", label: "Refunds", kind: "refund", tone: "", detail: `${formatMoney(status.refundedCents ?? 0, active)} returned.` },
        ].map((item) => {
          const value = status[item.key] ?? 0;
          return (
            <button
              key={item.key}
              type="button"
              className={`mcap-lic-status ${item.tone && value ? `is-${item.tone}` : ""}`}
              disabled={!value}
              {...bind({ title: item.label, subtitle: item.detail, rows: [{ label: "Count", value: formatNumber(value) }], footer: value ? "Click to see payments" : undefined })}
              onClick={() => {
                hide();
                onDrill({ type: "payments", title: item.label, filters: { kind: item.kind } });
              }}
            >
              <span className="mcap-lic-status-value">{formatNumber(value)}</span>
              <span className="mcap-lic-status-label">{item.label}</span>
            </button>
          );
        })}
      </div>
      {totals.testCents > 0 && (
        <p className="mcap-lic-axis-note">
          {formatMoney(totals.testCents, active)} of test payments are not counted above.
        </p>
      )}

      <ChartTooltip tip={tip} />
    </section>
  );
}
